import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Stack, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ShowContainer from '../../../../../components/material-ui/ShowContainer';
import Table from '../../../../../components/material-ui/adaptive/Table';
import MyTableCell from '../../../../../components/material-ui/adaptive/MyTableCell';
import { useTranslation } from 'react-i18next';
import AllCheckbox from '../../../../../components/material-ui/AllCheckbox';
import CusLabelSelect from '../../../../../components/material-ui/CusLabelSelect';
import CusInput from '../../../../../components/material-ui/CusInput';
import CusButton from '../../../../../components/material-ui/CusButton';
import { API_GAMEKIT } from '../../../../../api/gamekit';
import { useAppSelector } from '../../../../../store';
import { setSelectGame, setSourceNameById } from '../../../../../store/apps/gameProvidersSlice';
import { useDispatch } from 'react-redux';
import AllGameRow from './AllGameRow';
import LoaderContainer from '../../../../../components/material-ui/loader_container/LoaderContainer';
import { TEST_MODE } from '../../../../../enums/enums';
import { useDebounceValue } from 'usehooks-ts';
import { useVirtualizer } from '@tanstack/react-virtual';
import TypographyText from '../../../../../components/material-ui/table/TypographyText';

const STEP_PRIORITY = 10;

const AllGames = memo(
  ({ categoryName, selectCategoryId, updateCategoryGames }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const selectGameId = useAppSelector((state) => state.gameProviders.selectGame.id);
    const categoryId = useAppSelector((state) => state.gameProviders.categoryGames.categoryId);
    const categoryGames = useAppSelector((state) => state.gameProviders.categoryGames.list);

    const [sourceInfo, setSourceInfo] = useState({});
    const [sourcesOptions, setSourcesOptions] = useState([]);
    const [providersOptions, setProvidersOptions] = useState([{ label: 'All', value: '' }]);
    const [allGames, setAllGames] = useState([]);

    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounceValue(search, 300);
    const [selectSource, setSelectSource] = useState('');
    const [selectProvider, setSelectProvider] = useState('');
    const [selectIdList, setSelectIdList] = useState([]);
    const [providersHash, setProvidersHash] = useState({});
    const [loader, setLoader] = useState(true);

    const curCategoryGames = categoryId == selectCategoryId ? categoryGames : [];

    const { myGames, gamesUuidSet } = useMemo(() => {
      const myGames = [];
      const gamesUuidSet = new Set();
      curCategoryGames.forEach((el) => {
        if (el[4] == selectSource) {
          myGames.push(el);
          gamesUuidSet.add(el[3]);
        }
      });
      return { myGames, gamesUuidSet };
    }, [curCategoryGames, selectSource]);

    const maxPriority = useMemo(() => {
      let max = 1;
      for (let i = 0; i < curCategoryGames.length; i++) {
        const val = curCategoryGames[i][10];
        if (val > max) max = val;
      }
      return max;
    }, [curCategoryGames]);

    const currentGames = useMemo(() => {
      if (!allGames.length) return [];

      let selectedGames = allGames;

      if (debouncedSearch) {
        const needle = debouncedSearch.toLocaleLowerCase();
        selectedGames = selectedGames.filter((el) =>
          el.name?.toLocaleLowerCase().startsWith(needle)
        );
      }

      if (selectProvider) {
        selectedGames = selectedGames.filter((el) => el.provider == selectProvider);
      }

      return selectedGames.filter((el) => !gamesUuidSet.has(el.uuid));
    }, [allGames, debouncedSearch, selectProvider, gamesUuidSet]);

    const scrollRef = useRef(null);
    const ROW_HEIGHT = 40;

    const rowVirtualizer = useVirtualizer({
      count: currentGames.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: () => ROW_HEIGHT,
      overscan: 30
    });

    const virtualRows = rowVirtualizer.getVirtualItems();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom =
      virtualRows.length > 0
        ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
        : 0;

    const onGameClick = useCallback(
      (game, priority = 1) => {
        dispatch(
          setSelectGame({
            id: game.uuid,
            name: game.name,
            originName: game.name,
            uuid: game.uuid,
            source: selectSource,
            image: game.image,
            type: game.type,
            provider: game.provider,
            providerName: providersHash[game.provider]?.customName,
            hasFreeSpins: !!+game.has_freespins,
            freespinFullDay: !!+game.freespin_valid_until_full_day,
            priority,
            enabled: true,
            categoryId: game.categoryId,
            sourceGameId: game.sourceGameId,
            hit: false
          })
        );
      },
      [dispatch, selectSource, providersHash]
    );

    useEffect(() => {
      API_GAMEKIT.collection.source
        .list()
        .then((e) => {
          const list = e.data.list || {};
          const source = Object.keys(list);
          const sources = source?.map((el) => ({ label: list[el]?.name, value: el })) || [];

          const sourceNameById =
            source?.reduce((acc, el) => {
              acc[el] = list[el].name;
              return acc;
            }, {}) || {};

          setSourceInfo(list);
          setSourcesOptions([...sources]);
          setSelectSource(sources[0]?.value);
          dispatch(setSourceNameById(sourceNameById));

          if (!sources.length) setLoader(false);
        })
        .catch((e) => {
          console.error(e);
          setLoader(false);
        });
    }, []);

    useEffect(() => {
      const providerInfo = sourceInfo[selectSource];

      if (!providerInfo) return;
      const providersHash = providerInfo.providers;
      const origNames = Object.keys(providersHash);
      const providers =
        origNames?.map((el) => {
          const providerParam = providersHash[el];
          const cusLabel = `${providerParam.customName} (${providerParam.percent || 0}%) `;
          return { label: cusLabel, value: el };
        }) || [];

      providers.sort((l, r) => l.label.localeCompare(r.label));
      const providerO = [{ label: t('All'), value: '' }, ...providers];
      setProvidersOptions(providerO);
      setProvidersHash(providersHash);
    }, [selectSource]);

    useEffect(() => {
      if (TEST_MODE) {
        return;
      }
      if (!selectSource) return;
      setLoader(true);
      API_GAMEKIT.collection.source
        .games({ sourceId: selectSource })
        .then((e) => {
          const list = e.data.list;
          setAllGames(list);
        })
        .catch(console.error)
        .finally(() => setLoader(false));
    }, [selectSource]);

    const onAddCategoryGames = () => {
      const gameList = selectIdList.map((el) => currentGames[el]);

      API_GAMEKIT.collection.categories
        .includesGames({
          sourceId: selectSource,
          categoryId: selectCategoryId,
          gameList,
          priority: { start: maxPriority + STEP_PRIORITY, step: STEP_PRIORITY }
        })
        .then((e) => {
          const game = { ...gameList.at(), categoryId: selectCategoryId };
          setSelectIdList([]);
          updateCategoryGames();
          onGameClick(game, maxPriority + STEP_PRIORITY * gameList.length);
        })
        .catch(console.error);
    };

    const heightContainer = 'calc(100svh - 595px)';

    const providerParam = providersHash[selectProvider];
    const currencies = providerParam?.currencyList.join(',') || t('All');

    return (
      <Stack display={'flex'} direction={'column'} gap={1.5}>
        <Stack gap={1} px={2} pb={0.5}>
          <Typography fontWeight={900} fontSize={16}>
            {t('List of aggregator games')}
          </Typography>
        </Stack>
        <Stack display={'flex'} gap={2}>
          <Stack display={'flex'} direction={'column'} gap={0.5} px={0.5}>
            <Stack display={'flex'} direction={'row'} alignItems={'center'} width={'100%'} gap={1}>
              <Typography minWidth="110px">{t('Pool')}</Typography>

              <CusLabelSelect
                width={'100%'}
                options={sourcesOptions}
                value={selectSource}
                isVerySmall
                onChange={(val) => {
                  setSelectSource(val);
                  setSelectIdList([]);
                  setSelectProvider('');
                }}
                isTranslateOptions={false}
              />
            </Stack>
            <Stack display={'flex'} direction={'row'} alignItems={'center'} width={'100%'} gap={1}>
              <Typography minWidth="110px">{t('Provider')}</Typography>

              <CusLabelSelect
                width={'100%'}
                selectProps={{ slotProps: { input: { sx: { paddingY: 0.5 } } } }}
                options={providersOptions}
                value={selectProvider}
                isVerySmall
                onChange={(val) => {
                  setSelectProvider(val);
                  setSelectIdList([]);
                }}
                isTranslateOptions={false}
              />
            </Stack>

            <ShowContainer condition={selectProvider}>
              <Stack direction={'row'}>
                <Typography>
                  <Typography component={'span'}>{t('Currencies')}:</Typography>{' '}
                  <TypographyText>{currencies}</TypographyText>
                </Typography>
              </Stack>
            </ShowContainer>
          </Stack>

          <Stack gap={1}>
            <LoaderContainer
              loader={loader}
              boxSx={{ height: heightContainer, minHeight: `250px` }}>
              <TableContainer
                ref={scrollRef}
                sx={{
                  maxHeight: heightContainer,
                  overflow: 'auto',
                  minHeight: `250px`
                }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <MyTableCell sx={{ px: 2, py: 0 }}>
                        <Stack direction={'row'} gap={1}>
                          <Typography fontWeight={600} minWidth="110px">
                            <AllCheckbox
                              allListId={currentGames.map((_, indx) => indx)}
                              chosenList={selectIdList}
                              setChosenList={setSelectIdList}
                              checkBoxSx={{ paddingLeft: 0 }}
                            />

                            {t('Name')}
                          </Typography>

                          <CusInput
                            placeholder={'Search by name'}
                            value={search}
                            onChange={(e) => {
                              setSearch(e.target.value);
                              setSelectIdList([]);
                            }}
                            isVerySmall
                            width="100%"
                          />
                        </Stack>
                      </MyTableCell>
                    </TableRow>
                  </TableHead>
                  <ShowContainer condition={currentGames.length}>
                    <TableBody>
                      {paddingTop > 0 && (
                        <TableRow>
                          <MyTableCell style={{ padding: 0, border: 0, height: paddingTop }} />
                        </TableRow>
                      )}

                      {virtualRows.map((virtualRow) => {
                        const game = currentGames[virtualRow.index];
                        return (
                          <AllGameRow
                            game={game}
                            providersHash={providersHash}
                            gameId={game.id || game.uuid}
                            key={game.source + game.uuid}
                            indx={virtualRow.index}
                            selectGameId={selectGameId}
                            checked={selectIdList.includes(virtualRow.index)}
                            onGameClick={onGameClick}
                            setSelectIdList={setSelectIdList}
                          />
                        );
                      })}

                      {paddingBottom > 0 && (
                        <TableRow>
                          <MyTableCell style={{ padding: 0, border: 0, height: paddingBottom }} />
                        </TableRow>
                      )}
                    </TableBody>
                  </ShowContainer>
                </Table>
              </TableContainer>
            </LoaderContainer>
            <ShowContainer condition={!loader}>
              <CusButton
                text={t('Add to this category', { name: `"${categoryName}"` })}
                fullWidth
                variant="contained"
                sx={{ lineHeight: '110%' }}
                onClick={onAddCategoryGames}
                disabled={!selectIdList.length || !selectCategoryId}
              />
            </ShowContainer>
          </Stack>
        </Stack>
      </Stack>
    );
  },
  (prevProps, nextProps) => prevProps.selectCategoryId === nextProps.selectCategoryId
);

export default AllGames;
